import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tag } from "lucide-react";

/**
 * Modal de formulário para categoria de pagamento
 */
export default function PaymentCategoryFormModal({
  open,
  onOpenChange,
  category,
  onSave,
  loading,
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    active: true,
  });

  const [errors, setErrors] = useState({});

  // Preencher formulário quando editando
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        description: category.description || "",
        active: category.active !== false,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        active: true,
      });
    }
    setErrors({});
  }, [category, open]);

  /**
   * Validar formulário
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submeter formulário
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  /**
   * Atualizar campo do formulário
   */
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#c9332C]" />
            {category ? "Editar Categoria" : "Nova Categoria"}
          </DialogTitle>
          <DialogDescription>
            {category
              ? "Atualize as informações da categoria de pagamento"
              : "Preencha os dados para criar uma nova categoria de pagamento"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nome da Categoria <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Ex: Cartão de Crédito, PIX, Dinheiro..."
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              disabled={loading}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              placeholder="Descrição opcional da categoria..."
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={loading}
              rows={3}
            />
            <p className="text-xs text-gray-500">
              Adicione detalhes sobre esta categoria de pagamento
            </p>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="active">Status</Label>
            <Select
              value={formData.active ? "true" : "false"}
              onValueChange={(value) =>
                handleChange("active", value === "true")
              }
              disabled={loading}
            >
              <SelectTrigger id="active">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Ativo</SelectItem>
                <SelectItem value="false">Inativo</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Categorias inativas não estarão disponíveis para uso
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#c9332C] hover:bg-[#a02820]"
            >
              {loading ? "Salvando..." : category ? "Atualizar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
